import axios from "axios";
import { getApiUrlWithVersion } from "./environmentService";
import { getAuthUser } from "./authService";

export interface SelfInterviewQuestion {
  question: string;
  category: string;
  pillar: "news" | "personal" | "business" | "industry" | "custom";
  source: "ai" | "custom" | "web_search";
  answer_text?: string;
  answer_duration?: number;
  quality_score?: "Bad" | "Okay" | "Good" | "Excellent" | null;
  quality_feedback?: string | null;
  quality_suggestions?: string[];
  dismissed?: boolean;
  article_url?: string;
  article_title?: string;
  article_summary?: string;
}

export interface SelfInterview {
  id: number;
  user_id: number;
  organization_id: number;
  status: string;
  questions: SelfInterviewQuestion[];
  calendar_config: Record<string, string>;
  answered_count: number;
  ingredient_group_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface AssessmentResult {
  score: "Bad" | "Okay" | "Good" | "Excellent" | null;
  feedback: string | null;
  suggestions: string[];
}

export interface BuildEmailResult {
  edition_id: number;
  magic_link: string;
}

class SelfInterviewService {
  private getAuthHeaders(): object {
    return {
      Authorization: getAuthUser()?.accessToken,
    };
  }

  async list(): Promise<SelfInterview[]> {
    const response = await axios.get(
      `${getApiUrlWithVersion()}/self_interviews`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async create(): Promise<SelfInterview> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/self_interviews`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async show(id: number): Promise<SelfInterview> {
    const response = await axios.get(
      `${getApiUrlWithVersion()}/self_interviews/${id}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async update(id: number, data: Partial<Pick<SelfInterview, "questions" | "answered_count" | "calendar_config">>): Promise<SelfInterview> {
    const response = await axios.patch(
      `${getApiUrlWithVersion()}/self_interviews/${id}`,
      data,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async generateQuestions(params: { strategyDocumentId?: number; blueprintId?: number } = {}): Promise<SelfInterviewQuestion[]> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/self_interviews/generate_questions`,
      { strategy_document_id: params.strategyDocumentId, blueprint_id: params.blueprintId },
      { headers: this.getAuthHeaders() }
    );
    return response.data.questions;
  }

  async generateQuestionsAsync(interviewId: number, params: { strategyDocumentId?: number; blueprintId?: number; newsOnly?: boolean } = {}): Promise<void> {
    await axios.post(
      `${getApiUrlWithVersion()}/self_interviews/${interviewId}/generate_questions_async`,
      { strategy_document_id: params.strategyDocumentId, blueprint_id: params.blueprintId, news_only: params.newsOnly },
      { headers: this.getAuthHeaders() }
    );
  }

  async generateQuestionFromTopic(topic: string, params: { strategyDocumentId?: number; blueprintId?: number } = {}): Promise<SelfInterviewQuestion> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/self_interviews/generate_question_from_topic`,
      { topic, strategy_document_id: params.strategyDocumentId, blueprint_id: params.blueprintId },
      { headers: this.getAuthHeaders() }
    );
    return response.data.question;
  }

  async swapQuestion(currentQuestion: string, params: { strategyDocumentId?: number; blueprintId?: number; source?: string } = {}): Promise<SelfInterviewQuestion> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/self_interviews/swap_question`,
      { current_question: currentQuestion, strategy_document_id: params.strategyDocumentId, blueprint_id: params.blueprintId, source: params.source },
      { headers: this.getAuthHeaders() }
    );
    return response.data.question;
  }

  async sharpenAnswer(selfInterviewId: number, question: string, answerText: string): Promise<{ improved_text: string }> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/self_interviews/${selfInterviewId}/sharpen_answer`,
      { question, answer_text: answerText },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async assessAnswer(selfInterviewId: number, question: string, answerText: string): Promise<AssessmentResult> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/self_interviews/${selfInterviewId}/assess_answer`,
      { question, answer_text: answerText },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async saveSession(selfInterviewId: number, options?: { complete?: boolean; questions?: any[] }): Promise<SelfInterview & { ingredients_created?: number; ingredient_group_id?: number }> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/self_interviews/${selfInterviewId}/save_session`,
      { complete: options?.complete, questions: options?.questions },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async buildEmail(selfInterviewId: number, questionIndex: number, blueprintId: number, additionalInstructions?: string): Promise<BuildEmailResult> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/self_interviews/${selfInterviewId}/build_email`,
      { question_index: questionIndex, blueprint_id: blueprintId, additional_instructions: additionalInstructions },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }
}

const selfInterviewService = new SelfInterviewService();
export default selfInterviewService;
